<template>
    <div
        ref="container"
        :key="isVideo ? 'image' : `image-${isMounted}`"
        class="image-container video"
        :style="computedStyle"
        :class="[scaling, fill, keepAspectRatio ? 'keep-aspect-ratio' : ''].join(' ')"
    >
        <video
            v-if="isVideo"
            :controls="!autoplay"
            :autoplay
            :muted="autoplay"
            loop
            playsinline
        >
            <source :src="videoSource" type="video/mp4">
            Your browser does not support the video tag.
        </video>
        <img v-else-if="isServer" :alt="title" />
        <picture v-else>
            <source :srcset="webpSource" type="image/webp" />
            <img :src="jpegSource" :alt="title" />
        </picture>
    </div>
</template>
<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from '#imports';
const props = defineProps({
    id: {
        type: [String, Number],
        required: false,
        default: '',
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    name: {
        type: String,
        required: false,
        default: '',
    },
    ext: {
        type: String,
        required: false,
        default: '',
    },
    width: {
        type: Number,
        required: false,
        default: 0,
    },
    height: {
        type: Number,
        required: false,
        default: 0,
    },
    created: {
        type: String,
        required: false,
        default: '',
    },
    generic_url: { // eslint-disable-line vue/prop-name-casing
        type: String,
        required: false,
        default: '',
    },
    preview_url: { // eslint-disable-line vue/prop-name-casing
        type: String,
        required: false,
        default: '',
    },
    src: {
        type: String,
        required: false,
        default: '',
    },
    // actual props
    computeWidth: {
        type: Boolean,
        required: false,
        default: false,
    },
    fill: {
        type: String,
        required: false,
        validator: v => ['cover', 'contain'].indexOf(v) !== -1,
        default: 'contain',
    },
    scaling: {
        type: String,
        required: false,
        validator: v => ['width', 'height'].indexOf(v) !== -1,
        default: 'width',
    },
    keepAspectRatio: {
        type: Boolean,
        required: false,
        default: false,
    },
    autoplay: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const container = ref(null);
const debounce = ref(false);
const isMounted = ref(false);

const isServer = computed(() => import.meta.server);
const isVideo = computed(() => props.ext === 'mp4');
// Add #t to the URL so that iOS Safari shows the video preview
const videoSource = computed(() => `${props.generic_url.replace('width={width}', 'width=1920')}#t=0.001`);
const computedStyle = computed(() => {
    if (!isMounted.value) {
        return false;
    }

    if (isVideo.value) return 'aspect-ratio: 16 / 9';

    if (container.value) {
        const height = Math.round(container.value.getBoundingClientRect().height);

        if (props.scaling === 'height' && props.computeWidth) {
            return `width: ${Math.round(props.width/props.height * height)}px`;
        }
    }

    return '';
});
const source = computed(() => {
    if (!isMounted.value || !container.value) {
        return false;
    }

    const ratio = window?.devicePixelRatio || 1;
    let height, width;

    switch (props.scaling) {
        case 'height':
            height = Math.round(container.value.getBoundingClientRect().height);

            return props.generic_url.replace('?width={width}', `?height=${Math.round(height*ratio)}`);

        default:
        case 'width':
            width = Math.round(container.value.getBoundingClientRect().width);

            return props.generic_url.replace('?width={width}', `?width=${Math.round(width*ratio)}`);
    }
});
const webpSource = computed(() => source.value ? `${source.value}&format=webp` : false);
const jpegSource = computed(() => source.value ? `${source.value}&format=jpeg` : false);

function resize() {
    window.clearTimeout(debounce.value);

    debounce.value = window.setTimeout(() => {
        isMounted.value = Date.now();
    }, 400);
}
onMounted(() => {
    if (window) {
        window.addEventListener('resize', resize);
    }
    nextTick(() => {
        if (isMounted.value === false) {
            isMounted.value = Date.now();
        }
    });
});
onUnmounted(() => {
    window.removeEventListener('resize', resize);
});
</script>
<style scoped>
.image-container {
  position: relative;
  overflow: hidden;
}
.image-container.cover > * {
  object-fit: cover;
}
.image-container.contain > * {
  object-fit: contain;
}
.image-container:not(.keep-aspect-ratio) > * {
  position: absolute;
  left: 0;
  top: 0;
}
.image-container > * {
  width: 100%;
  height: 100%;
  max-width: 100%;
}
</style>
